// useStartupAnns.ts

import { useEffect, useState } from "react";
import { type StartupAnnConfig } from "./startupAnns.config";
import {
    type StartupAnnStateMap,
    pickStartupAnnToShow,
    recordStartupAnnEvent,
} from "./startupAnns.engine";
import { NS2 } from "../../../state/config";

const STORAGE_KEY = `${NS2}startupAnnState:v1`;

function loadStateMap(): StartupAnnStateMap {
    if (typeof window === "undefined") {
        return {} as StartupAnnStateMap;
    }

    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return {} as StartupAnnStateMap;
        return JSON.parse(raw) as StartupAnnStateMap;
    } catch {
        return {} as StartupAnnStateMap;
    }
}

function saveStateMap(map: StartupAnnStateMap) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch {
        // ignore quota or private mode errors
    }
}

export interface UseStartupAnnsOptions {
    noneWeight?: number;
    /**
     * When false, the hook will NOT pick or show any announcement.
     * Useful to delay showing until app-session is fully loaded.
     */
    enabled?: boolean;
}

export interface UseStartupAnnsResult {
    activeAnn: StartupAnnConfig | null;
    dismissActiveAnn: () => void;
}

export function useStartupAnns(
    options: UseStartupAnnsOptions = {},
): UseStartupAnnsResult {
    const [activeAnn, setActiveAnn] = useState<StartupAnnConfig | null>(null);

    useEffect(() => {
        // If explicitly disabled, make sure nothing is shown and bail out
        if (options.enabled === false) {
            setActiveAnn(null);
            return;
        }

        let stateMap = loadStateMap();

        console.log("[useStartupAnns] stateMap", stateMap);

        const picked = pickStartupAnnToShow(stateMap, {
            noneWeight: options.noneWeight,
            searchString: window.location.search,
        });

        if (!picked) {
            setActiveAnn(null);
            return;
        }

        // record impression immediately and persist
        stateMap = recordStartupAnnEvent(picked.id, "shown", stateMap);
        saveStateMap(stateMap);

        setActiveAnn(picked);
    }, [options.noneWeight, options.enabled]);

    const dismissActiveAnn = () => {
        setActiveAnn((current) => {
            if (!current) return null;

            const stateMap = loadStateMap();
            const nextMap = recordStartupAnnEvent(
                current.id,
                "dismissed",
                stateMap,
            );
            saveStateMap(nextMap);

            return null;
        });
    };

    return { activeAnn, dismissActiveAnn };
}
